import {FC, useCallback, useEffect, useState} from 'react'
import {
  useAffiliateChangeAccessMutation,
  useLazyGetAffiliateCountQuery,
  useLazyGetCommissionAmountQuery,
  useSubmitReferralCodeMutation,
} from '../../../../../services/cellcast.v2'
import {useDispatch, useSelector} from 'react-redux'
import {fetchAsyncUserDetails, getAllUserDetails} from '../Overview/OverviewSlice/UserDetailsSlice'
import {SubmitReferralCode, myReferralCode} from '../../../../../services/cellcast.v2.types'
import {Formik, Form, Field, ErrorMessage, FormikHelpers} from 'formik'
import * as Yup from 'yup'
import {Spinner} from 'react-bootstrap'
import {toast} from 'react-toastify'
import moment from 'moment-timezone'
import ToggleSwitch from 'react-bootstrap/Form'
import clsx from 'clsx'

type OverviewProps = {
  userData: any
}

const Overview: FC<OverviewProps> = (props) => {
  const dispatch = useDispatch()

  const {userData} = props

  // constant variable
  const validationSchema = Yup.object({
    code: Yup.string().min(4).required('Code is required'),
  })

  const initialValues: SubmitReferralCode = {
    code: '',
  }

  const [parentHaveAccess, setParentHaveAccess] = useState(false)

  // rtk actions
  const [submitCode, submitCodeResponse] = useSubmitReferralCodeMutation()
  const [updateAccess, updateAccessResponse] = useAffiliateChangeAccessMutation()
  const [getAffiliateCount, getAffiliateCountResponse] = useLazyGetAffiliateCountQuery()

  // action
  const handleSubmitCode = useCallback(
    (values: SubmitReferralCode, helpers: FormikHelpers<SubmitReferralCode>) => {
      submitCode(values).then((res) => {
        if ('data' in res) {
          if (res.data?.error && Object.keys(res.data?.error).length > 0) {
            toast.error(res?.data?.message)
          } else {
            dispatch(fetchAsyncUserDetails())
            toast.success(res?.data?.message)
          }
        } else if ('error' in res) {
          const resError = res.error as any
          if (resError.data?.message) {
            toast.error(resError.data?.message || '')
          } else {
            toast.error(res?.error.toString() || '')
          }
        }
      })
    },
    [dispatch, submitCode]
  )

  const handleCopy = useCallback((code: string) => {
    navigator.clipboard.writeText(code)
    toast.success('Copied!')
  }, [])

  const getStatusCode = (status: string) => {
    switch (status) {
      case myReferralCode.NotSet:
        return <span className='badge badge-light-info'>Not Set</span>
      case myReferralCode.WaitingApproval:
        return <span className='badge badge-light-warning'>Waiting Approval</span>
      case myReferralCode.Active:
        return <span className='badge badge-light-success'>Active</span>
      case myReferralCode.Inactive:
        return <span className='badge badge-light-danger'>Not Active</span>
      case myReferralCode.Rejected:
        return <span className='badge badge-light-danger'>Rejected</span>
    }
  }

  useEffect(() => {
    getAffiliateCount({})
  }, [])

  useEffect(() => {
    if (userData?.referralParentAccessStatus !== undefined) {
      setParentHaveAccess(userData?.referralParentAccessStatus === 'granted')
    }
  }, [userData])

  const getResponseAtLabel = (status: string) => {
    switch (status) {
      case myReferralCode.NotSet:
      case myReferralCode.WaitingApproval:
      case myReferralCode.Inactive:
        return <label>Approve/Reject At </label>
      case myReferralCode.Active:
        return <label>Approve At </label>
      case myReferralCode.Rejected:
        return <label>Reject At </label>
    }
  }

  const handleUpdate = (isGranted: boolean) => {
    let status = 'granted'
    if (!isGranted) {
      status = 'rejected'
    }

    updateAccess({status}).then((res) => {
      if ('data' in res) {
        if (res.data?.error && Object.keys(res.data?.error).length > 0) {
          toast.error(res?.data?.message)
        } else {
          dispatch(fetchAsyncUserDetails())
          toast.success(res?.data?.message)
        }
      } else if ('error' in res) {
        const resError = res.error as any
        if (resError.data?.message) {
          toast.error(resError.data?.message || '')
        } else {
          toast.error(res?.error.toString() || '')
        }
      }
    })
  }

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-header cursor-pointer'>
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0 fs-2'>Overview</h3>
        </div>
      </div>
      <div className='card-body border-top p-9'>
        {userData?.referralCode && (
          <div className='row'>
            <div className='col-lg-12 mb-5' style={{borderBottom: 'black dashed 1px '}}>
              <div className='row'>
                <div className='col-lg-2 mb-7 d-flex align-items-center'>
                  <h6 className='fw-bolder m-0 fs-3'>Used Referral Code</h6>
                </div>
                <div className='col-lg-4 mb-7'>
                  <div
                    className='col-lg-3 d-flex justify-content-center align-items-center'
                    style={{
                      fontSize: 15,
                      backgroundColor: '#f5f8fa',
                      padding: 10,
                      paddingLeft: 20,
                      paddingRight: 20,
                      borderRadius: 5,
                      minHeight: 5,
                    }}
                  >
                    {userData?.referralCode}
                  </div>
                </div>

                {userData?.referralParentAccessStatus === 'granted' ||
                userData?.referralParentAccessStatus === 'rejected' ? (
                  <>
                    <div className='col-lg-3 mb-7 d-flex align-items-center'>
                      <h6 className='fw-bolder m-0 fs-3'>Parent Access</h6>
                    </div>
                    <div className='col-lg-3 mb-7 d-flex align-items-center'>
                      <div
                        className={clsx({
                          'text-danger': true,
                          'me-5': true,
                        })}
                      >
                        Rejected
                      </div>
                      <ToggleSwitch.Check
                        onChange={(e) => {
                          handleUpdate(!parentHaveAccess)
                          setParentHaveAccess(!parentHaveAccess)
                        }}
                        type='switch'
                        id='custom-switch'
                        checked={parentHaveAccess}
                      />
                      <div
                        className={clsx({
                          'text-success': true,
                          'ms-5': true,
                        })}
                      >
                        Granted
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        )}

        <div className='row'>
          <div
            className='col-lg-12 mb-5 mt-5'
            style={
              userData?.myReferralCodes &&
              Array.isArray(userData?.myReferralCodes) &&
              userData?.myReferralCodes.length > 0
                ? {borderBottom: 'black dashed 1px '}
                : {}
            }
          >
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmitCode}
            >
              {({isSubmitting}) => (
                <Form>
                  <div className='row mb-5'>
                    <div className='col-lg-12 mb-7'>
                      <h4 className='fw-bolder m-0 fs-3'>Submit New Referral Code</h4>
                    </div>
                    {userData?.myReferralCodeStatus === 'rejected' && (
                      <div className='col-lg-12 mb-3'>
                        <span className='badge badge-light-danger'>
                          your code is rejected, please try submit again
                        </span>
                      </div>
                    )}
                    <div className='col-lg-3'>
                      <div className='form-group'>
                        <Field
                          type='text'
                          id='code'
                          name='code'
                          className='form-control form-control-flush ps-10 rounded border-gray-200 border'
                          placeholder='Enter your referral code'
                        />
                        <ErrorMessage name='code' component='div' className='text-danger' />
                      </div>
                    </div>
                    <div className='col-lg-4'>
                      <button className='btn btn-primary' type='submit'>
                        {submitCodeResponse.isLoading ? <Spinner size='sm' /> : 'Submit Code'}
                      </button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>

        {userData?.myReferralCodes &&
          Array.isArray(userData?.myReferralCodes) &&
          userData?.myReferralCodes.length > 0 && (
            <div className='row'>
              <div className='col-lg-12 mb-5 mt-5'>
                <h6 className='fw-bolder m-0 fs-3'>My Referral Code</h6>
              </div>
              {userData?.myReferralCodes.map((el: any) => (
                <div className='col-lg-6 mb-5'>
                  <div className='row'>
                    <div
                      className='col-lg-3 d-flex justify-content-center align-items-center'
                      style={{
                        fontSize: 20,
                        backgroundColor: '#f5f8fa',
                        padding: 10,
                        paddingLeft: 20,
                        paddingRight: 20,
                        borderRadius: 5,
                        minHeight: 80,
                      }}
                    >
                      <span className='text-truncate'>{el?.code}</span>
                    </div>
                    <div className='col-lg-8 ps-5 d-flex flex-column'>
                      <div className='row mb-2'>
                        <div className='col-md-5'>
                          <label>Status </label>
                        </div>
                        <div className='col-md-7'>{getStatusCode(el?.status)}</div>
                      </div>
                      <div className='row'>
                        <div className='col-md-5'>{getResponseAtLabel(el?.status)}</div>
                        <div className='col-md-7'>
                          <label className='ms-1'>
                            {(el?.responseAt && el?.status === myReferralCode.Active) ||
                            el?.status === myReferralCode.Rejected
                              ? moment(el?.responseAt).format('DD MMMM YYYY - hh:mm')
                              : '-'}
                          </label>
                        </div>
                      </div>
                      <div className='row'>
                        <div className='col-md-5 mt-5'>
                          <button
                            className='btn btn-sm btn-primary'
                            onClick={() => handleCopy(el?.code)}
                          >
                            <i className='bi bi-clipboard'></i> copy code
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  )
}

export default Overview
